/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IAuditDetail, IAuditSummary } from '../../components/Audit/types';
import { API_URLS } from '../../constants/ApiUrls';
import { eosGet } from '../../services/EosApi';
import { UKForecastGasDayResponse } from '../types/forecast.types';
import { getSelectedMarketId, getSelectedPortfolioId } from './dashboard';

interface AuditDataState {
  loading: boolean;
  data: IAuditSummary[];
  error: string | null;
}

interface AuditDetailState {
  loading: boolean;
  data: IAuditDetail[] | UKForecastGasDayResponse | null;
  error: string | null;
}

export interface IEOSAuditState {
  AuditData: AuditDataState;
  AuditDetail: AuditDetailState;
}

const initialState: IEOSAuditState = {
  AuditData: {
    loading: false,
    data: [] as IAuditSummary[],
    error: null,
  },
  AuditDetail: {
    loading: false,
    data: null,
    error: null,
  },
};

export const getAuditData = createAsyncThunk(
  'audit/getAuditData',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const selectedPortfolioId = getSelectedPortfolioId(
        state as Parameters<typeof getSelectedPortfolioId>[0],
      );

      const { selectedContractId, selectedCountryId } = (
        state as {
          dashboardEOS?: {
            scenarioFilters?: {
              selectedContractId: number | null;
              selectedCountryId: number | null;
            };
          };
        }
      ).dashboardEOS?.scenarioFilters ?? {
        selectedContractId: null,
        selectedCountryId: null,
      };
      const url = `${API_URLS.AUDIT_LOG_DATA}?portfolioId=${selectedPortfolioId}&marketId=${selectedCountryId}&supplyContractId=${selectedContractId}`;
      const response = await eosGet(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch audit data');
    }
  },
);

interface GetAuditDetailParams {
  auditLogId: number;
  pageNumber?: number;
  pageSize?: number;
}

export const getAuditDetail = createAsyncThunk(
  'audit/getAuditDetail',
  async (params: GetAuditDetailParams, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const selectedMarketId = getSelectedMarketId(
        state as Parameters<typeof getSelectedMarketId>[0],
      );
      const { auditLogId, pageNumber = 1, pageSize = 5 } = params;
      const url = `${API_URLS.AUDIT_GET_AUDIT_DETAIL}?auditLogId=${auditLogId}&marketId=${selectedMarketId}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
      const response = await eosGet(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch audit details',
      );
    }
  },
);

const auditSlice = createSlice({
  name: 'audit',
  initialState,
  reducers: {
    resetAuditDetailState: (state: IEOSAuditState) => {
      state.AuditDetail = initialState.AuditDetail;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAuditData.pending, (state: IEOSAuditState) => {
        state.AuditData.loading = true;
        state.AuditData.data = [];
        state.AuditData.error = null;
      })
      .addCase(
        getAuditData.fulfilled,
        (state: IEOSAuditState, action: { payload: IAuditSummary[] }) => {
          state.AuditData.loading = false;
          state.AuditData.data = action.payload;
          state.AuditData.error = null;

          if (action.payload.length === 0) {
            state.AuditDetail = initialState.AuditDetail;
          }
        },
      )
      .addCase(getAuditData.rejected, (state: IEOSAuditState, action: { payload }) => {
        state.AuditData.loading = false;
        state.AuditData.error = action.payload;
      });

    builder
      .addCase(getAuditDetail.pending, (state: IEOSAuditState) => {
        state.AuditDetail.loading = true;
      })
      .addCase(
        getAuditDetail.fulfilled,
        (state: IEOSAuditState, action: { payload: IAuditDetail[] | UKForecastGasDayResponse }) => {
          state.AuditDetail.loading = false;
          state.AuditDetail.data = action.payload;
          state.AuditDetail.error = null;
        },
      )
      .addCase(getAuditDetail.rejected, (state: IEOSAuditState, action: { payload }) => {
        state.AuditDetail.loading = false;
        state.AuditDetail.error = action.payload;
      });
  },
});

export const getAuditDataState = (state: { EOSAudit: IEOSAuditState }) => state.EOSAudit.AuditData;

export const getAuditDetailState = (state: { EOSAudit: IEOSAuditState }) =>
  state.EOSAudit.AuditDetail;

export const { resetAuditDetailState } = auditSlice.actions;

export default auditSlice.reducer;
